import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck, Mail, Clock, FileLock2, CheckCircle2, Send, Loader2, ScrollText,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Public data-deletion (GDPR Right to Erasure) request page.
 *
 * Three states:
 *   1. Idle — form rendered
 *   2. Confirming — user has clicked "Continue", review screen shown
 *   3. Submitted — success card with the reference id
 *
 * Anti-abuse:
 *   - hidden honeypot input `website_url` (display:none + aria-hidden +
 *     tabindex=-1) — bots that fill every field will get silently dropped
 *   - `form_render_ts` epoch ms set on mount, posted with the form. The
 *     backend rejects submissions <3s after render.
 *   - the backend enforces 5 requests / day per source IP.
 */
export default function DataDeletionPage() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const formRenderTs = useRef(Date.now());
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { request_id }
  // Honeypot. MUST stay empty. Real browsers can't see this field.
  const [honeypot, setHoneypot] = useState("");

  useEffect(() => {
    document.title = "Data deletion · Feel Greats";
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
    return () => { document.title = "Feel Greats — Smart Health Advisor"; };
  }, [user]);

  const emailValid = useMemo(() => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()), [email]);

  const onContinue = (e) => {
    e?.preventDefault?.();
    if (!emailValid) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!acknowledge) {
      toast.error("Please tick the acknowledgement box to continue.");
      return;
    }
    setConfirming(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const endpoint = user ? "/me/data-deletion/request" : "/data-deletion/request";
      const body = user
        ? { reason, website_url: honeypot, form_render_ts: formRenderTs.current, locale: user.locale || "en" }
        : { email: email.trim(), full_name: name.trim(), reason, website_url: honeypot, form_render_ts: formRenderTs.current, locale: "en" };
      const { data } = await api.post(endpoint, body);
      setSubmitted({ request_id: data?.request_id || "—" });
      setConfirming(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Submission failed. Please try again or email support@feelgreats.co.uk.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Submitted state ----
  if (submitted) {
    return (
      <main className="bg-leaf-pattern min-h-[calc(100vh-4rem)] py-12" data-testid="data-deletion-page">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <Card className="rounded-2xl border-emerald-100" data-testid="data-deletion-success">
            <CardContent className="p-8 text-center">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-700 mb-5">
                <CheckCircle2 className="w-7 h-7" strokeWidth={1.6} />
              </span>
              <h1 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light tracking-tight">
                Your request has been logged.
              </h1>
              <p className="text-emerald-900/80 mt-3 leading-relaxed">
                We've sent a confirmation email to <span className="font-semibold">{user?.email || email}</span>{" "}
                with the next steps. Our team typically reviews requests within 1 business day and
                completes the erasure within 30 days at the latest.
              </p>
              {submitted.request_id ? (
                <div className="mt-6 inline-block rounded-xl bg-emerald-50/70 border border-emerald-100 px-4 py-2 text-emerald-900/80 text-sm">
                  Reference: <code className="text-emerald-700 font-semibold" data-testid="data-deletion-reference">{submitted.request_id}</code>
                </div>
              ) : null}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Button
                  type="button"
                  onClick={() => navigate("/")}
                  className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6"
                  data-testid="data-deletion-home"
                >
                  Back to home
                </Button>
                <a
                  href="mailto:support@feelgreats.co.uk?subject=Data%20deletion%20follow-up"
                  className="text-sm text-emerald-700 hover:text-emerald-800 underline"
                  data-testid="data-deletion-followup-email"
                >
                  Email support@feelgreats.co.uk
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  // ---- Idle / confirming state ----
  return (
    <main className="bg-leaf-pattern min-h-[calc(100vh-4rem)] py-12" data-testid="data-deletion-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <header className="mb-8 sm:mb-10 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
            Privacy · GDPR
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-emerald-950 font-light tracking-tight mt-3">
            Delete my data.
          </h1>
          <p className="text-emerald-900/70 mt-3 max-w-xl mx-auto leading-relaxed">
            You're always in control of your information on Feel Greats. This page explains
            exactly what we'll remove, what we may keep, and how to make a request.
          </p>
        </header>

        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          <InfoTile icon={FileLock2} title="What we delete" body="Your account, assessment answers, BMI report, dashboard history, engagement data, referral data and email subscriptions." />
          <InfoTile icon={Clock} title="How long it takes" body="Reviewed within 1 business day. Complete erasure within 30 days at the latest — usually much sooner." />
          <InfoTile icon={ShieldCheck} title="What we may keep" body="A minimal accounting record (invoices, refunds) where UK law requires retention — typically 6 years for HMRC." />
        </div>

        {confirming ? (
          // ---- Confirm screen ----
          <Card className="rounded-2xl border-emerald-100" data-testid="data-deletion-confirm">
            <CardContent className="p-6 sm:p-8 space-y-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                <ScrollText className="w-3.5 h-3.5" /> Final step — please review
              </div>
              <h2 className="font-display text-2xl text-emerald-950">Confirm your request</h2>
              <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-4 text-sm text-emerald-900 space-y-1">
                <Row label="Name" value={user ? (user.name || "—") : (name || "—")} />
                <Row label="Email" value={user ? user.email : email} />
                <Row label="Reason" value={reason || "(not provided)"} />
              </div>
              <p className="text-sm text-emerald-900/80 leading-relaxed">
                By submitting, you confirm that you're the rightful owner of this email
                address. We may briefly contact you to verify ownership before the data is
                erased.
              </p>
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button
                  type="button"
                  onClick={onSubmit}
                  disabled={submitting}
                  className="cta-shine rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-5"
                  data-testid="data-deletion-confirm-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {submitting ? "Sending…" : "Submit deletion request"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirming(false)}
                  className="rounded-full border-emerald-200 text-emerald-900 hover:bg-emerald-50"
                  data-testid="data-deletion-back"
                >
                  Go back
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          // ---- Idle form ----
          <Card className="rounded-2xl border-emerald-100" data-testid="data-deletion-form-card">
            <CardContent className="p-6 sm:p-8">
              {user ? (
                <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-3 text-sm text-emerald-900 mb-5" data-testid="data-deletion-auth-note">
                  Signed in as <span className="font-semibold">{user.email}</span>. We'll process the deletion for this account.
                </div>
              ) : (
                <div className="rounded-xl bg-amber-50/70 border border-amber-100 p-3 text-sm text-amber-900 mb-5">
                  Not signed in? You can still file a request below. If your email matches an
                  existing account we'll process it automatically once we've verified ownership.
                  <span className="hidden sm:inline">
                    {" "}Already have an account?{" "}
                    <Link to="/login?next=/data-deletion" className="underline text-amber-900 hover:text-amber-700">
                      Sign in
                    </Link>{" "}for a faster flow.
                  </span>
                </div>
              )}

              <form onSubmit={onContinue} noValidate className="space-y-4">
                {/* Honeypot — hidden from real users + screen readers */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
                  <label htmlFor="website_url">Website</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>

                {!user && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dd-name">Your name</Label>
                      <Input
                        id="dd-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1 rounded-xl border-emerald-200"
                        placeholder="(optional)"
                        autoComplete="name"
                        data-testid="data-deletion-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="dd-email">
                        Email <span className="text-rose-700">*</span>
                      </Label>
                      <Input
                        id="dd-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 rounded-xl border-emerald-200"
                        placeholder="you@example.com"
                        autoComplete="email"
                        data-testid="data-deletion-email"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <Label htmlFor="dd-reason">Anything you'd like us to know? <span className="text-emerald-900/60 text-xs font-normal">(optional)</span></Label>
                  <Textarea
                    id="dd-reason"
                    rows={4}
                    value={reason}
                    onChange={(e) => setReason(e.target.value.slice(0, 800))}
                    className="mt-1 rounded-xl border-emerald-200"
                    placeholder="Helps us improve. Skipping this is fine — your request will be processed regardless."
                    data-testid="data-deletion-reason"
                  />
                  <div className="text-xs text-emerald-900/55 mt-1">{reason.length}/800</div>
                </div>

                <label className="flex items-start gap-2 text-sm text-emerald-900/85 cursor-pointer select-none" data-testid="data-deletion-ack-label">
                  <Checkbox
                    checked={acknowledge}
                    onCheckedChange={(v) => setAcknowledge(!!v)}
                    className="mt-0.5 border-emerald-300 data-[state=checked]:bg-emerald-700"
                    data-testid="data-deletion-ack"
                  />
                  <span>
                    I understand that Feel Greats may retain a minimal accounting record where
                    UK law requires (e.g. HMRC retention for invoices and refunds), and that I
                    can read more in the{" "}
                    <Link to="/privacy" className="underline text-emerald-700 hover:text-emerald-800">Privacy Policy</Link>.
                  </span>
                </label>

                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <Button
                    type="submit"
                    className="cta-shine rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-5"
                    data-testid="data-deletion-continue"
                  >
                    Continue
                  </Button>
                  <a
                    href="mailto:support@feelgreats.co.uk?subject=Data%20deletion%20request"
                    className="text-sm text-emerald-700 hover:text-emerald-800 underline"
                    data-testid="data-deletion-email-link"
                  >
                    or email support@feelgreats.co.uk
                  </a>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <Card className="mt-6 rounded-2xl border-emerald-100 bg-white/60" data-testid="data-deletion-policy">
          <CardContent className="p-6 sm:p-8 prose prose-sm max-w-none text-emerald-900/85 leading-relaxed">
            <h2 className="font-display text-xl text-emerald-950 mt-0 mb-3">Our deletion commitment</h2>
            <p className="m-0">
              Under UK GDPR and the Data Protection Act 2018 (and analogous laws worldwide), you
              have the right to ask us to erase personal data we hold about you. Feel Greats is
              committed to:
            </p>
            <ul className="mt-3 space-y-2">
              <li>Acknowledging your request within 1 business day.</li>
              <li>Completing erasure within 30 days at the latest — usually within a week.</li>
              <li>Confirming with you by email once your data has been removed.</li>
              <li>Retaining only the minimum legally-required records (e.g. invoices for HMRC).</li>
              <li>Never selling or sharing your wellness data with third parties.</li>
            </ul>
            <p className="mt-3 mb-0">
              If you're not satisfied with our response, you have the right to complain to the UK
              Information Commissioner's Office (ICO) at{" "}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">ico.org.uk</a>.
            </p>
            <p className="mt-4 mb-0 text-xs text-emerald-900/60">
              For full details on what we collect and why, see our{" "}
              <Link to="/privacy" className="underline text-emerald-700">Privacy Policy</Link>.
              For any other concerns, write to{" "}
              <a href="mailto:support@feelgreats.co.uk" className="underline text-emerald-700">support@feelgreats.co.uk</a>.
            </p>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

function InfoTile({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-emerald-100 bg-white p-4 sm:p-5">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 mb-3">
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </span>
      <div className="font-semibold text-emerald-950 text-sm">{title}</div>
      <div className="text-xs text-emerald-900/75 mt-1 leading-relaxed">{body}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[88px_1fr] gap-2 sm:gap-3">
      <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold pt-0.5">{label}</div>
      <div className="break-words">{value}</div>
    </div>
  );
}
